import { useMemo, useState, type FormEvent } from 'react';
import { isValidCanvasName } from '@canvas/utils';
import {
  CANVAS_PRESETS,
  QUICK_START_FILTERS,
  type CanvasPreset,
  type PresetAudience,
} from '@/data/presets';
import {
  DEFAULT_DPI,
  LENGTH_UNITS,
  LENGTH_UNIT_LABELS,
  type LengthUnit,
  clampDpi,
  convertLength,
  convertLengthForDpiChange,
  formatPixelPair,
  fromPixels,
  inputStepForUnit,
  toPixels,
} from '@/lib/lengthUnits';

interface NewProjectModalProps {
  busy?: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: string;
    description: string;
    width: number;
    height: number;
    tags: string[];
  }) => Promise<void>;
}

const MIN_PX = 100;
const MAX_PX = 8000;

export default function NewProjectModal({
  busy = false,
  onClose,
  onCreate,
}: NewProjectModalProps) {
  const [filter, setFilter] = useState<'all' | PresetAudience>('all');
  const [presetId, setPresetId] = useState('presentation');
  const [name, setName] = useState('Untitled project');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState<LengthUnit>('px');
  const [dpi, setDpi] = useState(DEFAULT_DPI);
  const [width, setWidth] = useState(1920);
  const [height, setHeight] = useState(1080);
  const [error, setError] = useState<string | null>(null);

  const presets = useMemo(
    () =>
      CANVAS_PRESETS.filter(
        (preset) => filter === 'all' || preset.audience === filter || preset.audience === 'all'
      ),
    [filter]
  );

  const selectPreset = (preset: CanvasPreset) => {
    setPresetId(preset.id);
    setWidth(fromPixels(preset.width, unit, dpi));
    setHeight(fromPixels(preset.height, unit, dpi));
    if (!name.trim() || name.startsWith('Untitled') || CANVAS_PRESETS.some((p) => p.name === name)) {
      setName(preset.name);
    }
    setError(null);
  };

  const changeUnit = (next: LengthUnit) => {
    setWidth(convertLength(width, unit, next, dpi));
    setHeight(convertLength(height, unit, next, dpi));
    setUnit(next);
    setError(null);
  };

  const changeDpi = (raw: number) => {
    const next = clampDpi(raw);
    setWidth(convertLengthForDpiChange(width, unit, dpi, next));
    setHeight(convertLengthForDpiChange(height, unit, dpi, next));
    setDpi(next);
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!isValidCanvasName(name.trim())) {
      setError('Project name must be 1–255 characters.');
      return;
    }

    const selected = CANVAS_PRESETS.find((p) => p.id === presetId);
    let pxW: number;
    let pxH: number;

    if (presetId === 'custom') {
      pxW = toPixels(width, unit, dpi);
      pxH = toPixels(height, unit, dpi);
    } else {
      pxW = selected?.width ?? toPixels(width, 'px', dpi);
      pxH = selected?.height ?? toPixels(height, 'px', dpi);
    }

    if (pxW < MIN_PX || pxH < MIN_PX || pxW > MAX_PX || pxH > MAX_PX) {
      setError(
        `Canvas size must be between ${MIN_PX} and ${MAX_PX} pixels (currently ${formatPixelPair(pxW, pxH)}).`
      );
      return;
    }

    await onCreate({
      name: name.trim(),
      description: description.trim(),
      width: pxW,
      height: pxH,
      tags: selected ? [selected.id, selected.audience] : ['custom'],
    });
  };

  const step = inputStepForUnit(unit);
  const previewPx =
    presetId === 'custom'
      ? formatPixelPair(toPixels(width, unit, dpi), toPixels(height, unit, dpi))
      : null;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="new-project-title">New project</h2>
        <p className="subtitle">
          Choose a canvas size suited for school work or professional use.
        </p>

        {error && <div className="error-banner">{error}</div>}

        <form className="modal-form" onSubmit={submit}>
          <div className="preset-row">
            {QUICK_START_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`preset-chip ${filter === item.id ? 'active' : ''}`}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="size-grid">
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`size-option ${presetId === preset.id ? 'active' : ''}`}
                onClick={() => selectPreset(preset)}
              >
                <strong>{preset.name}</strong>
                <span>
                  {preset.width} × {preset.height} · {preset.description}
                </span>
              </button>
            ))}
          </div>

          <div className="field">
            <label htmlFor="project-name">Project name</label>
            <input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="project-description">Description (optional)</label>
            <textarea
              id="project-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Final poster for design studio, client pitch deck…"
            />
          </div>

          {presetId === 'custom' && (
            <>
              <div className="size-unit-row">
                <div className="field">
                  <label htmlFor="size-unit">Unit</label>
                  <select
                    id="size-unit"
                    value={unit}
                    onChange={(e) => changeUnit(e.target.value as LengthUnit)}
                  >
                    {LENGTH_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {LENGTH_UNIT_LABELS[u]}
                      </option>
                    ))}
                  </select>
                </div>
                {unit !== 'px' && (
                  <div className="field">
                    <label htmlFor="size-dpi">DPI</label>
                    <input
                      id="size-dpi"
                      type="number"
                      min={72}
                      max={600}
                      step={1}
                      value={dpi}
                      onChange={(e) => changeDpi(Number(e.target.value))}
                    />
                  </div>
                )}
              </div>
              <div className="dim-row">
                <div className="field">
                  <label htmlFor="width">Width ({unit})</label>
                  <input
                    id="width"
                    type="number"
                    min={0}
                    step={step}
                    value={Number.isFinite(width) ? width : 0}
                    onChange={(e) => setWidth(Number(e.target.value))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="height">Height ({unit})</label>
                  <input
                    id="height"
                    type="number"
                    min={0}
                    step={step}
                    value={Number.isFinite(height) ? height : 0}
                    onChange={(e) => setHeight(Number(e.target.value))}
                  />
                </div>
              </div>
              {previewPx && (
                <p className="field-hint size-px-preview">
                  ≈ {previewPx}
                  {unit !== 'px' ? ` at ${clampDpi(dpi)} DPI` : ''}
                </p>
              )}
            </>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create & open'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
